import { InfoCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'react-tooltip'

import FeedIcon from '@Icons/feed-url.svg?react'
import ImportFileRectIcon from '@Icons/import-a-file-rect.svg?react'
import ImportFileIcon from '@Icons/import-a-file.svg?react'
import AddProductOneByOneIcon from '@Icons/plus-rect.svg?react'
import ShopifyIcon from '@Icons/shopify-icon-2.svg?react'
import UpdateExistingRectIcon from '@Icons/update-existing-rect.svg?react'

import AddBatchProductSource from '../components/product/manual/AddBatchProductSource'
import AddSingleProductSource from '../components/product/manual/AddSingleProductSource'
import EditBatchProductSource from '../components/product/manual/EditBatchProductSource'
import ProductSourceFeed from '../components/ProductSourceFeed'
import ProductSourceManual from '../components/ProductSourceManual'
import ProductSourceShopify from '../components/ProductSourceShopify'
import { type ProductSource } from '../types/product'

export const getProductManagements = (): ProductSource[] => [
  {
    id: 'feed',
    title: 'Feed Integration',
    description: 'Sync your catalog automatically via a product feed URL.',
    icon: <FeedIcon className="mr-3" />,
    // Until now this card was a disabled "Coming Soon". It is available in
    // beta, but not to configure: clicking it asks us to set the feed up.
    badge: 'NEW',
  },
  {
    id: 'manual',
    title: 'Manual Management',
    description: 'Add or update products via Excel, or add them one by one.',
    icon: <ImportFileIcon className="mr-3" />,
  },
  {
    id: 'shopify',
    title: 'Shopify',
    description: 'Import directly from your connected Shopify store.',
    icon: <ShopifyIcon className="w-[36px] h-[36px] p-[4px] mr-1" />,
  },
]

export const getProductManagement = (id: string): ProductSource | null =>
  getProductManagements().find((source) => source.id === id) || null

export const getProductManagementSection = (
  id: string,
  props: any = {},
): JSX.Element =>
  ({
    feed: <ProductSourceFeed {...props} />,
    manual: <ProductSourceManual {...props} />,
    shopify: <ProductSourceShopify {...props} />,
  })?.[id] ?? <></>

export const getManualProductSources = (): ProductSource[] => [
  {
    id: 'import',
    title: 'Import a file',
    description: 'Upload an Excel to add new products in bulk.',
    icon: <ImportFileRectIcon />,
  },
  {
    id: 'add_single',
    title: 'Add One by One',
    description: 'Enter product details manually, one product at a time.',
    icon: <AddProductOneByOneIcon />,
  },
  {
    id: 'update',
    title: (
      <>
        Update Existing Products
        <InfoCircleOutlined
          className="cursor-pointer ml-2 text-primary-600"
          data-tooltip-id={`tooltip-description-update-products`}
        />
        <Tooltip
          id={`tooltip-description-update-products`}
          content="Only certain fields can be updated. Changes to identifiers or core product fields (such as Product ID, Name, Barcode, Product Code) will not be applied."
          className="!w-40 whitespace-break-spaces text-sm"
          place="top"
        />
      </>
    ),
    description: (
      <>
        Download a template, fill in updates, and re-upload.
        <br />
        You can update your own URL, cost and additional costs for your existing
        products.
      </>
    ),
    icon: <UpdateExistingRectIcon />,
  },
]

export const getManualProductSource = (id: string): ProductSource | null =>
  getManualProductSources().find((source) => source.id === id) || null

export const getManualProductSourceSection = (
  id: string,
  props: any = {},
): JSX.Element =>
  ({
    import: <AddBatchProductSource {...props} />,
    add_single: <AddSingleProductSource {...props} />,
    update: <EditBatchProductSource {...props} />,
  })?.[id] ?? <></>

export const getShopifyProductSources = (): ProductSource[] => [
  {
    id: 'import',
    title: 'Import from Shopify',
    description: 'Browse and import products from your Shopify store.',
    icon: <ImportFileRectIcon />,
  },
  {
    id: 'update',
    title: (
      <>
        Update Existing Products
        <InfoCircleOutlined
          className="cursor-pointer ml-2 text-primary-600"
          data-tooltip-id={`tooltip-description-update-products`}
        />
        <Tooltip
          id={`tooltip-description-update-products`}
          content="Only certain fields can be updated. Changes to identifiers or core product fields (such as Product ID, Name, Barcode, Product Code) will not be applied."
          className="!w-40 whitespace-break-spaces text-sm"
          place="top"
        />
      </>
    ),
    description: (
      <>
        Download a template, fill in updates, and re-upload.
        <br />
        You can update cost and additional costs for your existing products.
      </>
    ),
    icon: <UpdateExistingRectIcon />,
  },
]
